import { AudiencePageLayout, type AudienceContent } from '../audience/AudiencePageLayout';

const content: Record<'de' | 'en', AudienceContent> = {
  de: {
    title: 'qtplan – Ressourcen & Infrastrukturkosten | Kosten direkt im Architekturdiagramm',
    metaDesc: 'Infrastrukturkosten an Canvas-Elemente heften. Hetzner-Produktkatalog, automatische Aggregation in Gruppen, Canvas-Übersicht mit Gesamtkosten. Kein Spreadsheet, kein Account nötig.',
    hero: {
      h1: 'Infrastrukturkosten gehören in den Plan — nicht ins Spreadsheet.',
      lead: 'Server werden auf dem Canvas skizziert, Kosten in einer Excel-Tabelle gepflegt. Zwei Quellen, null Zusammenhang. Wenn sich die Architektur ändert, aktualisiert niemand das Spreadsheet. Resources in qtplan heften Kostendaten direkt an Canvas-Elemente. Gruppen aggregieren automatisch. Die Canvas-Übersicht zeigt was die Infrastruktur kostet — nicht als veraltete Tabelle sondern als Teil des Plans.',
    },
    bento: {
      heading: 'Kosten dort wo die Architektur lebt',
      items: [
        {
          span: 2,
          label: 'KATALOG',
          title: 'Hetzner-Server mit einem Klick hinzufügen',
          body: 'Ein Produktkatalog mit vorkonfigurierten Server-Typen. Cloud Server (ARM und AMD, Shared und Dedicated vCPU), Dedicated Server, Managed Server, Load Balancer, Storage — alle mit korrekten Monatskosten, vCPU, RAM und Disk. Server auswählen, Resource wird mit allen Feldern erstellt. Eine Suchfunktion durchsucht alle Ebenen des Katalogs gleichzeitig. Erweiterbar für weitere Provider.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'FELDER',
          title: 'Typisierte Datenfelder',
          body: 'Jede Resource hat strukturierte Felder — Währung, Zahl oder Text. Optionales Intervall (monatlich, jährlich, stündlich) und Einheit. Kosten werden als Währung gespeichert, vCPU als Zahl, CPU-Modell als Text. Die Struktur bestimmt wie Werte dargestellt und aggregiert werden.',
        },
        {
          span: 1,
          label: 'AGGREGATION',
          title: 'Summe, Durchschnitt, Min, Max',
          body: 'Jedes Feld hat eine Aggregationsregel. Kosten werden summiert, Durchschnittswerte berechnet, Maxima ermittelt — automatisch. Die Regel wird einmal pro Feld definiert und gilt überall: in Gruppen, in der Übersicht, im Footer.',
          variant: 'accent',
        },
        {
          span: 2,
          label: 'CANVAS',
          title: 'Kosten an Architekturdiagramme heften',
          body: 'Resources werden per Drag-and-Drop aus der Sidebar auf Canvas-Elemente gezogen. Ein Server-Symbol bekommt seine Kosten, eine Datenbank ihre Storage-Kosten. Das Infrastrukturdiagramm zeigt nicht nur die Architektur sondern auch was sie kostet. Änderungen an der Architektur aktualisieren die Kosten sofort — weil beide am selben Element hängen.',
        },
        {
          span: 1,
          label: 'ÜBERSICHT',
          title: 'Alle Kosten auf einen Blick',
          body: 'Die Canvas-Übersicht zeigt alle Resources als sortierbare, filterbare Tabelle. Gesamtkosten pro Monat, Ressourcenanzahl, belegte Entities. Kategoriefilter, Suchfeld und dynamische Spalten. Die Totalzeile am Ende summiert was die Infrastruktur kostet.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'GRUPPEN',
          title: 'Cluster-Kosten aggregieren',
          body: 'Canvas-Gruppen fassen Elemente zusammen — und deren Kosten. Drei Server in einer Gruppe? Der Gruppen-Footer zeigt die Summe. Kosten, vCPU, RAM — alles was sich summieren lässt, wird aggregiert und direkt auf dem Canvas sichtbar.',
        },
        {
          span: 1,
          label: 'SCOPE',
          title: 'Global, Board oder Canvas',
          body: 'Resources gelten global, pro Board oder pro Canvas. Ein Server der auf mehreren Boards genutzt wird, wird einmal definiert. Canvas-spezifische Resources leben dort wo sie gebraucht werden. Der Scope bestimmt Sichtbarkeit und Wiederverwendbarkeit.',
        },
        {
          span: 1,
          label: 'NUTZUNG',
          title: 'Board-weite Statistiken',
          body: 'Pro Resource zeigt qtplan wie oft sie auf dem Board verwendet wird. Auf welchen Canvases, an welchen Entities, mit welchen aggregierten Gesamtwerten. Das macht sichtbar welche Server tatsächlich geplant sind — und welche vergessen wurden.',
        },
        {
          span: 2,
          label: 'KEIN SPREADSHEET',
          title: 'Integration statt Separation',
          body: 'Infrastrukturkosten gehören nicht in eine separate Tabelle. Wenn sich der Plan ändert, ändern sich die Kosten mit. Kein Vergessen, kein Nachpflegen, kein "das Spreadsheet war nicht aktuell". Resources in qtplan sind Teil des Plans — nicht ein Anhang den niemand aktualisiert. Die Kosten leben dort wo die Architektur lebt.',
          variant: 'muted',
        },
      ],
    },
    narrative: {
      heading: 'Warum Infrastrukturkosten im Planungstool leben müssen',
      paragraphs: [
        'Die meisten Teams pflegen Infrastrukturkosten in einer separaten Tabelle. Excel, Google Sheets, Confluence — irgendwo abseits der eigentlichen Planung. Das funktioniert so lange niemand etwas ändert. Sobald ein Server dazukommt, eine Instanz hochskaliert oder ein Service wegfällt, müsste jemand das Spreadsheet aktualisieren. In der Praxis passiert das nicht. Nach drei Monaten stimmen die Zahlen nicht mehr und niemand merkt es.',
        'Resources in qtplan lösen das indem Kosten dort leben wo die Architektur lebt — auf dem Canvas. Ein Hetzner CAX31 wird aus dem Produktkatalog hinzugefügt, per Drag-and-Drop auf das Server-Symbol gezogen und die monatlichen Kosten sind Teil des Diagramms. Keine zweite Quelle, kein manuelles Abgleichen. Die Sidebar zeigt alle Resources des aktuellen Canvas, gruppiert nach Scope. Ein Klick öffnet die Detail-Ansicht mit allen Feldern und Board-weiten Nutzungsstatistiken.',
        'Gruppen machen Cluster-Kosten sichtbar. Drei Application-Server in einer Gruppe? Der Footer zeigt die Gesamtkosten. Ein Kubernetes-Cluster mit fünf Nodes? Summe der vCPU, RAM und monatlichen Kosten — automatisch berechnet, nicht manuell gezählt. Wenn ein Server hinzukommt oder wegfällt, aktualisiert sich die Aggregation sofort.',
        'Die Canvas-Übersicht fasst alles zusammen: eine sortierbare Tabelle mit allen Resources, gefiltert nach Kategorie oder Feldtyp, mit Totalzeile. Das ist die Ansicht für den Moment in dem jemand fragt "Was kostet unsere Infrastruktur pro Monat?" Die Antwort ist ein Klick entfernt — und sie stimmt, weil sie direkt aus dem Plan kommt statt aus einer Tabelle die vor Wochen das letzte Mal angefasst wurde.',
      ],
    },
    cta: {
      heading: 'Erste Infrastrukturkosten erfassen.',
      body: 'Kein Account. Keine Cloud. Alle Daten bleiben lokal im Browser. Canvas erstellen, Server aus dem Katalog hinzufügen und erleben wie Infrastrukturkosten Teil des Plans werden.',
    },
  },
  en: {
    title: 'qtplan – Resources & Infrastructure Costs | Costs Right in the Architecture Diagram',
    metaDesc: 'Attach infrastructure costs to canvas elements. Hetzner product catalog, automatic aggregation in groups, canvas overview with total costs. No spreadsheet, no account required.',
    hero: {
      h1: 'Infrastructure costs belong in the plan — not in a spreadsheet.',
      lead: 'Servers are sketched on the canvas, costs maintained in an Excel spreadsheet. Two sources, zero connection. When the architecture changes, nobody updates the spreadsheet. Resources in qtplan attach cost data directly to canvas elements. Groups aggregate automatically. The canvas overview shows what the infrastructure costs — not as an outdated table but as part of the plan.',
    },
    bento: {
      heading: 'Costs where the architecture lives',
      items: [
        {
          span: 2,
          label: 'CATALOG',
          title: 'Add Hetzner servers with one click',
          body: 'A product catalog with pre-configured server types. Cloud servers (ARM and AMD, shared and dedicated vCPU), dedicated servers, managed servers, load balancers, storage — all with correct monthly costs, vCPU, RAM and disk. Select a server, the resource is created with all fields. A search function searches all levels of the catalog simultaneously. Extensible for additional providers.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'FIELDS',
          title: 'Typed data fields',
          body: 'Every resource has structured fields — currency, number, or text. Optional interval (monthly, yearly, hourly) and unit. Costs are stored as currency, vCPU as number, CPU model as text. The structure determines how values are displayed and aggregated.',
        },
        {
          span: 1,
          label: 'AGGREGATION',
          title: 'Sum, average, min, max',
          body: 'Every field has an aggregation rule. Costs are summed, averages calculated, maxima determined — automatically. The rule is defined once per field and applies everywhere: in groups, in the overview, in the footer.',
          variant: 'accent',
        },
        {
          span: 2,
          label: 'CANVAS',
          title: 'Attach costs to architecture diagrams',
          body: 'Resources are dragged from the sidebar onto canvas elements. A server icon gets its costs, a database its storage costs. The infrastructure diagram shows not just the architecture but also what it costs. Changes to the architecture update costs immediately — because both are attached to the same element.',
        },
        {
          span: 1,
          label: 'OVERVIEW',
          title: 'All costs at a glance',
          body: 'The canvas overview shows all resources as a sortable, filterable table. Total costs per month, resource count, occupied entities. Category filters, search field and dynamic columns. The totals row at the bottom sums up what the infrastructure costs.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'GROUPS',
          title: 'Aggregate cluster costs',
          body: 'Canvas groups combine elements — and their costs. Three servers in a group? The group footer shows the total. Costs, vCPU, RAM — everything that can be summed is aggregated and visible directly on the canvas.',
        },
        {
          span: 1,
          label: 'SCOPE',
          title: 'Global, board, or canvas',
          body: 'Resources apply globally, per board, or per canvas. A server used across multiple boards is defined once. Canvas-specific resources live where they are needed. The scope determines visibility and reusability.',
        },
        {
          span: 1,
          label: 'USAGE',
          title: 'Board-wide statistics',
          body: 'Per resource, qtplan shows how often it is used on the board. On which canvases, attached to which entities, with which aggregated total values. This makes visible which servers are actually planned — and which ones were forgotten.',
        },
        {
          span: 2,
          label: 'NO SPREADSHEET',
          title: 'Integration over separation',
          body: 'Infrastructure costs do not belong in a separate table. When the plan changes, costs change with it. No forgetting, no double maintenance, no "the spreadsheet was outdated." Resources in qtplan are part of the plan — not an appendix that nobody updates. Costs live where the architecture lives.',
          variant: 'muted',
        },
      ],
    },
    narrative: {
      heading: 'Why infrastructure costs must live in the planning tool',
      paragraphs: [
        'Most teams maintain infrastructure costs in a separate table. Excel, Google Sheets, Confluence — somewhere apart from the actual planning. This works as long as nobody changes anything. As soon as a server is added, an instance scaled up, or a service removed, someone would need to update the spreadsheet. In practice, that does not happen. After three months the numbers are wrong and nobody notices.',
        'Resources in qtplan solve this by placing costs where the architecture lives — on the canvas. A Hetzner CAX31 is added from the product catalog, dragged onto the server icon, and the monthly costs become part of the diagram. No second source, no manual reconciliation. The sidebar shows all resources of the current canvas, grouped by scope. One click opens the detail view with all fields and board-wide usage statistics.',
        'Groups make cluster costs visible. Three application servers in a group? The footer shows the total costs. A Kubernetes cluster with five nodes? Sum of vCPU, RAM, and monthly costs — calculated automatically, not counted manually. When a server is added or removed, the aggregation updates immediately.',
        'The canvas overview brings everything together: a sortable table with all resources, filtered by category or field type, with a totals row. This is the view for the moment when someone asks "What does our infrastructure cost per month?" The answer is one click away — and it is correct, because it comes directly from the plan instead of a table that was last touched weeks ago.',
      ],
    },
    cta: {
      heading: 'Capture first infrastructure costs.',
      body: 'No account. No cloud. All data stays local in the browser. Create a canvas, add servers from the catalog, and experience how infrastructure costs become part of the plan.',
    },
  },
};

export function ResourcesFeaturePage({ lang }: { lang: 'de' | 'en' }) {
  return <AudiencePageLayout lang={lang} slug="resources" content={content[lang]} basePath="features" />;
}
